/**
 * Concurrency overview that doubles as the timeline's navigator (#946).
 *
 * A linear 24h axis at page width cannot show both a 22-second run and a
 * 27-minute one, so the lanes below are zoomable and this ribbon — which always
 * shows the whole horizon — is how you steer. Drag across it to zoom.
 *
 * Continuous connectors form a separate grey baseline band beneath the event
 * band. The event band carries NO stroke: an outline would trace the top of the
 * baseline everywhere and paint the continuous hum in the event colour.
 */
import React, { useCallback, useRef } from 'react';

export interface ConcurrencyRibbonProps {
  eventDepth: number[];
  baselineDepth: number[];
  windowStart: number;
  windowEnd: number;
  viewStart: number;
  viewEnd: number;
  /** Called with a range on drag, or null when a click should reset the zoom. */
  onZoom: (start: number | null, end?: number) => void;
}

/** Step-area path from `series` stacked on top of `baseline`. */
function areaPath(series: number[], baseline: number[], peak: number, height: number): string {
  const top = 10;
  const y = (v: number) => height - (v / peak) * (height - top);
  let d = `M 0 ${y(baseline[0] ?? 0)}`;
  series.forEach((v, i) => {
    const yy = y(v + (baseline[i] ?? 0));
    d += ` L ${i} ${yy} L ${i + 1} ${yy}`;
  });
  for (let i = series.length - 1; i >= 0; i--) {
    const yy = y(baseline[i] ?? 0);
    d += ` L ${i + 1} ${yy} L ${i} ${yy}`;
  }
  return `${d} Z`;
}

export const ConcurrencyRibbon: React.FC<ConcurrencyRibbonProps> = ({
  eventDepth,
  baselineDepth,
  windowStart,
  windowEnd,
  viewStart,
  viewEnd,
  onZoom,
}) => {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dragFrom = useRef<number | null>(null);

  const width = eventDepth.length;
  const height = 100;
  const total = eventDepth.map((v, i) => v + (baselineDepth[i] ?? 0));
  const peak = Math.max(1, ...total);
  const hasBaseline = baselineDepth.some((v) => v > 0);
  const zoomed = viewStart > windowStart || viewEnd < windowEnd;

  const fraction = useCallback((clientX: number) => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    dragFrom.current = fraction(e.clientX);

    const finish = (ev: MouseEvent) => {
      window.removeEventListener('mouseup', finish);
      const from = dragFrom.current;
      dragFrom.current = null;
      if (from === null) return;
      const to = fraction(ev.clientX);
      const lo = Math.min(from, to);
      const hi = Math.max(from, to);
      if (hi - lo < 0.006) {
        onZoom(null);
        return;
      }
      const span = windowEnd - windowStart;
      onZoom(windowStart + lo * span, windowStart + hi * span);
    };

    window.addEventListener('mouseup', finish);
    e.preventDefault();
  };

  const pct = (ms: number) => ((ms - windowStart) / (windowEnd - windowStart)) * 100;

  return (
    <div
      ref={surfaceRef}
      data-testid="ribbon-surface"
      onMouseDown={onMouseDown}
      className="relative h-[76px] cursor-ew-resize overflow-hidden rounded-md border bg-muted"
    >
      <svg
        viewBox={`0 0 ${Math.max(width, 1)} ${height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        className="absolute inset-0 block h-full w-full"
      >
        <path
          data-testid="ribbon-baseline-area"
          d={areaPath(baselineDepth, new Array(width).fill(0), peak, height)}
          className="fill-muted-foreground/30"
        />
        <path
          data-testid="ribbon-event-area"
          d={areaPath(eventDepth, baselineDepth, peak, height)}
          className="fill-primary/60"
        />
      </svg>

      <div className="pointer-events-none absolute left-2 top-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        peak {Math.max(0, ...total)}
        {hasBaseline && ` · baseline ${Math.max(0, ...baselineDepth)}`}
      </div>
      <div className="pointer-events-none absolute right-2 top-1.5 text-[10px] text-muted-foreground">
        drag to zoom
      </div>

      {zoomed && (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 bg-background/60"
            style={{ width: `${pct(viewStart)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 bg-background/60"
            style={{ left: `${pct(viewEnd)}%` }}
          />
          <div
            data-testid="ribbon-viewport"
            className="pointer-events-none absolute inset-y-0 border-x-2 border-foreground bg-foreground/5"
            style={{
              left: `${pct(viewStart)}%`,
              width: `${Math.max(pct(viewEnd) - pct(viewStart), 0.4)}%`,
            }}
          />
        </>
      )}
    </div>
  );
};
